import { useCallback } from 'react';

import { Clips } from '@air/api';
import { type Board } from '@air/api/types';
import { CANVAS_IMAGE_NODE_SPACING, getAddToCanvasMenuState } from '@air/feature-edit-canvas';
import { useToasts } from '@air/provider-toast';
import { convertUnknownToError, reportErrorToBugsnag } from '@air/utils-error';

import { CANVAS_CLIP_GRID_COLUMNS, clipsToAppNodes } from '~/components/Modals/AddToCanvasModal/clipsToAppNodes';
import { useAddToCanvasModal } from '~/components/Modals/AddToCanvasModal/useAddToCanvasModal';
import { useDraftCanvas } from '~/hooks/canvases/useDraftCanvas';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { fetchAllBoardClips, MAX_BOARD_CLIPS_FOR_CANVAS } from '~/utils/canvases/fetchAllBoardClips';
import { getCanvasEligibleClips } from '~/utils/canvases/getCanvasEligibleClips';

const inFlightBoardIds = new Set<string>();

const pluralAssets = (count: number) => (count === 1 ? 'asset' : 'assets');

export const usePrivateAddAllBoardAssetsToCanvas = () => {
  const { currentWorkspace, currentWorkspaceRef } = useCurrentWorkspace();
  const { dismissToast, showToast } = useToasts();
  const { showAddToCanvasModal } = useAddToCanvasModal();
  const { openDraftCanvas } = useDraftCanvas();
  const workspaceId = currentWorkspace?.id;

  const addAllBoardAssetsToCanvas = useCallback(
    (board: Board) => {
      if (!workspaceId || inFlightBoardIds.has(board.id)) {
        return;
      }

      inFlightBoardIds.add(board.id);
      const loadingToastId = showToast('Loading assets from this board…');
      // The callback closes over the workspace used for search. The ref is the live one after an in-SPA switch.
      const isStillInWorkspace = () => currentWorkspaceRef.current?.id === workspaceId;

      void fetchAllBoardClips({ boardId: board.id, search: Clips.search, workspaceId })
        .then(({ clips, exceedsCap, total }) => {
          if (!isStillInWorkspace()) {
            return;
          }

          if (exceedsCap) {
            showToast(
              `This board has ${total} assets. Add all to Canvas supports up to ${MAX_BOARD_CLIPS_FOR_CANVAS} at a time.`,
            );
            return;
          }

          if (clips.length === 0) {
            showToast('This board has no assets to add');
            return;
          }

          const eligibleClips = getCanvasEligibleClips(clips);

          if (eligibleClips.length === 0) {
            const reason =
              getAddToCanvasMenuState(clips).disabledReason ?? 'Nothing on this board can be added to a Canvas';

            showToast(reason);
            return;
          }

          const skippedCount = clips.length - eligibleClips.length;

          if (skippedCount > 0) {
            showToast(`${skippedCount} ${pluralAssets(skippedCount)} on this board can't be added to a Canvas`);
          }

          showAddToCanvasModal({
            ...(skippedCount > 0 ? { skippedCount } : {}),
            clips: eligibleClips,
            layout: 'wrap',
            onNewCanvas: () => {
              if (!isStillInWorkspace()) {
                return;
              }

              openDraftCanvas({
                analyticsContext: {
                  board_id: board.id,
                  source: 'board_context_menu',
                },
                boardId: board.id,
                initialNodes: clipsToAppNodes(eligibleClips, {
                  columns: CANVAS_CLIP_GRID_COLUMNS,
                  spacing: CANVAS_IMAGE_NODE_SPACING,
                }),
                viewedFrom: 'board_context_menu',
              });
            },
            sourceWorkspaceId: workspaceId,
            viewedFrom: 'board_context_menu',
          });
        })
        .catch((error: unknown) => {
          if (!isStillInWorkspace()) {
            return;
          }

          showToast('Could not load this board’s assets', { color: 'red' });
          reportErrorToBugsnag({
            context: 'Failed to load board assets for add all to canvas',
            error: convertUnknownToError(error),
          });
        })
        .finally(() => {
          dismissToast(loadingToastId);
          inFlightBoardIds.delete(board.id);
        });
    },
    [currentWorkspaceRef, dismissToast, openDraftCanvas, showAddToCanvasModal, showToast, workspaceId],
  );

  return { addAllBoardAssetsToCanvas };
};
